import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FactCheckOutlined as ReviewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { integrationApiService, StalePricingProduct } from '../services/integrationApi';
import { useBrandRegistry } from '../hooks/useBrandRegistry';
import { resolveBrandDisplayName } from '../utils/brandIdentity';
import { brandDisplayName } from '../utils/brandDisplay';
import { PageContainer, PageHeader, StatusChip, TableSkeleton } from '../components/ui';

function parseDaysParam(raw: string | null): number {
  const n = Number(raw);
  if (n === 7 || n === 14 || n === 30) return n;
  return 7;
}

type SortId = 'staleDays' | 'brandId' | 'sku' | 'lastCheckedAt';

function parseSortParam(raw: string | null): SortId {
  if (raw === 'brandId' || raw === 'sku' || raw === 'lastCheckedAt' || raw === 'staleDays') return raw;
  return 'staleDays';
}

function defaultSortDir(sort: SortId): 'asc' | 'desc' {
  return sort === 'brandId' || sort === 'sku' ? 'asc' : 'desc';
}

function parseSortDir(raw: string | null, sort: SortId): 'asc' | 'desc' {
  if (raw === 'asc' || raw === 'desc') return raw;
  return defaultSortDir(sort);
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const AdminPricingFreshnessPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: brandRegistry } = useBrandRegistry();
  const [days, setDays] = useState<number>(() => parseDaysParam(searchParams.get('days')));
  const [brandId, setBrandId] = useState<string>(() => {
    const brand = (searchParams.get('brand') || '').trim().toLowerCase();
    return brand || 'all';
  });
  const [sortBy, setSortBy] = useState<SortId>(() => parseSortParam(searchParams.get('sort')));
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() =>
    parseSortDir(searchParams.get('dir'), parseSortParam(searchParams.get('sort')))
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    const nextDays = parseDaysParam(searchParams.get('days'));
    const nextBrand = (searchParams.get('brand') || '').trim().toLowerCase() || 'all';
    const nextSort = parseSortParam(searchParams.get('sort'));
    const nextDir = parseSortDir(searchParams.get('dir'), nextSort);
    if (days !== nextDays) setDays(nextDays);
    if (brandId !== nextBrand) setBrandId(nextBrand);
    if (sortBy !== nextSort) setSortBy(nextSort);
    if (sortDir !== nextDir) setSortDir(nextDir);
  }, [searchParams]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (days !== 7) next.set('days', String(days));
    if (brandId !== 'all') next.set('brand', brandId);
    if (sortBy !== 'staleDays') next.set('sort', sortBy);
    if (!(sortBy === 'staleDays' && sortDir === 'desc')) next.set('dir', sortDir);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [days, brandId, sortBy, sortDir, searchParams, setSearchParams]);

  const brands = useMemo(() => {
    return (brandRegistry || [])
      .map((b: { slug?: string; id?: string; brandId?: string; name?: string; brandName?: string }) => ({
        id: String(b.slug || b.id || b.brandId || '').toLowerCase(),
        name: String(b.name || b.brandName || b.slug || b.id || ''),
      }))
      .filter((b) => b.id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandRegistry]);

  const filters = {
    days,
    brandId: brandId === 'all' ? undefined : brandId,
    limit: 500,
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['pricing-freshness', filters],
    queryFn: () => integrationApiService.fetchStalePricingProducts(filters),
  });

  const toggleSort = (id: SortId) => {
    if (sortBy === id) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(id);
    setSortDir(id === 'brandId' || id === 'sku' ? 'asc' : 'desc');
  };

  const rows = useMemo(() => {
    const list = [...(data?.data || [])];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'brandId':
          cmp = a.brandId.localeCompare(b.brandId);
          break;
        case 'sku':
          cmp = a.sku.localeCompare(b.sku);
          break;
        case 'lastCheckedAt':
          cmp = a.lastCheckedMs - b.lastCheckedMs;
          break;
        default:
          cmp = (a.staleDays ?? 9999) - (b.staleDays ?? 9999);
      }
      return cmp * dir || a.sku.localeCompare(b.sku);
    });
    return list;
  }, [data?.data, sortBy, sortDir]);

  const onExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      await integrationApiService.downloadStalePricingCsv(filters);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const brandLabel = (id: string, catalogBrand?: string) =>
    brandDisplayName(resolveBrandDisplayName(catalogBrand || id, brands));

  return (
    <PageContainer>
      <PageHeader
        title="Pricing Freshness"
        description="Live catalog products whose manufacturer price has not been re-checked in the selected window."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => void onExport()}
              disabled={exporting || (!isLoading && !rows.length)}
            >
              Export CSV
            </Button>
          </Stack>
        }
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Stale after</InputLabel>
            <Select
              label="Stale after"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            >
              <MenuItem value="all">All brands</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {brandDisplayName(b.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {isLoading && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
          role="status"
          aria-label="Loading summary"
        >
          {[96, 132, 120, 156, 184, 84].map((width, i) => (
            <Skeleton key={i} variant="rounded" width={width} height={32} sx={{ borderRadius: 4 }} />
          ))}
        </Stack>
      )}

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip
            label={`${summary.stale.toLocaleString()} stale`}
            color={summary.stale > 0 ? 'warning' : 'success'}
            variant="outlined"
          />
          <Chip label={`${summary.neverChecked.toLocaleString()} never checked`} variant="outlined" />
          <Chip label={`${summary.missingUrl.toLocaleString()} missing URL`} variant="outlined" />
          <Chip
            label={`${summary.repeatedFailures.toLocaleString()} failing repeatedly`}
            color={summary.repeatedFailures > 0 ? 'error' : 'default'}
            variant="outlined"
          />
          <Tooltip title="Catalog products scanned for this report: every product that is not discontinued, archived or a placeholder, for the selected brand or for all brands with an active scraper. Products without a manufacturer source URL are included (they show up as Missing URL), and brands without an active scraper are not scanned, so this can differ from the total active products elsewhere.">
            <Chip label={`${summary.scanned.toLocaleString()} active products scanned`} variant="outlined" />
          </Tooltip>
          <Chip
            label={`${Object.keys(summary.byBrand || {}).length.toLocaleString()} brands`}
            variant="outlined"
          />
          {rows.some((row) => row.stagedProductId) && (
            <Chip
              label={`${rows.filter((row) => row.stagedProductId).length.toLocaleString()} with staged review`}
              color="info"
              variant="outlined"
            />
          )}
        </Stack>
      )}

      {data?.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the {data.count.toLocaleString()} stalest of {data.totalStale.toLocaleString()} products. Export CSV or filter by brand for the rest.
        </Alert>
      )}

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError('')}>
          {exportError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || 'Failed to load pricing freshness report'}
        </Alert>
      )}

      {isLoading ? (
        <Paper>
          <TableSkeleton rows={8} columns={8} />
        </Paper>
      ) : rows.length === 0 ? (
        <Alert severity="success">
          No live catalog products are older than {days} days
          {brandId !== 'all' ? ` for ${brandLabel(brandId)}` : ''}.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === 'brandId' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'brandId'}
                    direction={sortBy === 'brandId' ? sortDir : 'asc'}
                    onClick={() => toggleSort('brandId')}
                  >
                    Brand
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'sku' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'sku'}
                    direction={sortBy === 'sku' ? sortDir : 'asc'}
                    onClick={() => toggleSort('sku')}
                  >
                    SKU
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 280 }}>Name</TableCell>
                <TableCell align="right">Retail</TableCell>
                <TableCell sortDirection={sortBy === 'lastCheckedAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'lastCheckedAt'}
                    direction={sortBy === 'lastCheckedAt' ? sortDir : 'desc'}
                    onClick={() => toggleSort('lastCheckedAt')}
                  >
                    Last checked
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={sortBy === 'staleDays' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'staleDays'}
                    direction={sortBy === 'staleDays' ? sortDir : 'desc'}
                    onClick={() => toggleSort('staleDays')}
                  >
                    Days stale
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 140 }}>Notes</TableCell>
                <TableCell sx={{ width: 96 }}>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: StalePricingProduct) => {
                const catalogBrand = brandLabel(row.brandId, row.brand);
                return (
                <TableRow key={row.productId} hover>
                  <TableCell>{catalogBrand}</TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/app/products?search=${encodeURIComponent(row.sku)}&brand=${encodeURIComponent(catalogBrand)}`}
                    >
                      {row.sku}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ minWidth: 280 }}>{row.name || '—'}</TableCell>
                  <TableCell align="right">{formatMoney(row.retail)}</TableCell>
                  <TableCell>{formatWhen(row.lastCheckedAt)}</TableCell>
                  <TableCell align="right">
                    {row.staleDays == null ? (
                      <StatusChip label="Never" tone="caution" size="small" />
                    ) : (
                      <Typography
                        variant="body2"
                        color={row.staleDays > 14 ? 'error.main' : 'warning.main'}
                        fontWeight={600}
                      >
                        {row.staleDays}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {row.missingUrl && <Chip size="small" color="error" label="Missing URL" />}
                      {row.failCount >= 3 && (
                        <Chip size="small" color="error" label={`${row.failCount} failures`} />
                      )}
                      {!row.missingUrl && row.failCount < 3 && row.lastCheckedAt == null && (
                        <Chip size="small" label="Never checked" />
                      )}
                      {row.stagedProductId && (
                        <Chip size="small" color="info" label={row.stagedStatus || 'staged'} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: 96 }}>
                    {row.stagedProductId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReviewIcon />}
                        component={RouterLink}
                        to={`/app/admin/staged-products?id=${encodeURIComponent(row.stagedProductId)}`}
                      >
                        Review
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export default AdminPricingFreshnessPage;
